import { capitalize, css, styled } from '@mui/material';

import { badgeClasses } from './theme';

import type { BadgeProps } from './types';

// 복잡한 variant+color 스타일 헬퍼 함수
const getVariantColorStyles = (
  variant: BadgeProps['variant'],
  color: Exclude<BadgeProps['color'], undefined>,
  theme: any,
) => {
  // theme.ts의 styleOverrides에서 이전된 완전한 스타일 로직
  const variantColorKey = `${variant}${capitalize(color)}`;

  // 모든 variant+color 조합 스타일 (원본 theme.ts에서 완전히 이전)
  const styleMap: Record<string, any> = {
    // ===== FILLED VARIANTS =====
    filledGray: css`
      background-color: ${theme.color['surface/neutral/strong/default']};
      color: ${theme.color['text/inverse/default']};
    `,
    filledGreen: css`
      background-color: ${theme.color['decorativeSurface/stronger/green']};
      color: ${theme.color['text/inverse/default']};
    `,
    filledRed: css`
      background-color: ${theme.color['decorativeSurface/stronger/red']};
      color: ${theme.color['text/inverse/default']};
    `,
    filledYellow: css`
      background-color: ${theme.color['decorativeSurface/stronger/yellow']};
      color: ${theme.color['text/inverse/default']};
    `,
    filledBlue: css`
      background-color: ${theme.color['decorativeSurface/stronger/blue']};
      color: ${theme.color['text/inverse/default']};
    `,
    filledPurple: css`
      background-color: ${theme.color['decorativeSurface/stronger/purple']};
      color: ${theme.color['text/inverse/default']};
    `,
    filledLightGray: css`
      background-color: ${theme.color['surface/neutral/weak/default']};
      color: ${theme.color['text/default']};
    `,
    filledInverse: css`
      background-color: ${theme.color['surface/default']};
      color: ${theme.color['text/default']};
    `,
    filledBrand: css`
      background-color: ${theme.color['surface/accent/default']};
      color: ${theme.color['text/default']};
    `,
    filledBlack: css`
      background-color: ${theme.color['text/default']};
      color: ${theme.color['text/inverse/default']};
    `,
    filledMagenta: css`
      background-color: ${theme.color['decorativeSurface/stronger/magenta']};
      color: ${theme.color['text/inverse/default']};
    `,
    filledSky: css`
      background-color: ${theme.color['decorativeSurface/stronger/sky']};
      color: ${theme.color['text/inverse/default']};
    `,
    filledOrange: css`
      background-color: ${theme.color['decorativeSurface/stronger/orange']};
      color: ${theme.color['text/inverse/default']};
    `,
    filledLightGreen: css`
      background-color: ${theme.color['decorativeSurface/stronger/teal']};
      color: ${theme.color['text/inverse/default']};
    `,

    // ===== TINT VARIANTS =====
    tintGray: css`
      box-shadow: inset 0 0 0 1px #1316191a;
      background-color: ${theme.color['surface/neutral/weak/default']};
      color: ${theme.color['text/weaker/default']};
    `,
    tintGreen: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/green']};
      background-color: ${theme.color['decorativeSurface/weakest/green']};
      color: ${theme.color['decorativeText/weak/green']};
    `,
    tintRed: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/red']};
      background-color: ${theme.color['decorativeSurface/weakest/red']};
      color: ${theme.color['decorativeText/weak/red']};
    `,
    tintYellow: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/yellow']};
      background-color: ${theme.color['decorativeSurface/weakest/yellow']};
      color: ${theme.color['decorativeText/weak/yellow']};
    `,
    tintBlue: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/blue']};
      background-color: ${theme.color['decorativeSurface/weakest/blue']};
      color: ${theme.color['decorativeText/weak/blue']};
    `,
    tintPurple: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/purple']};
      background-color: ${theme.color['decorativeSurface/weakest/purple']};
      color: ${theme.color['decorativeText/weak/purple']};
    `,
    tintLightGray: css`
      box-shadow: inset 0 0 0 1px #1316191a;
      background-color: ${theme.color['surface/neutral/weak/default']};
      color: ${theme.color['text/default']};
    `,
    tintInverse: css`
      box-shadow: inset 0 0 0 1px ${theme.color['border/default']};
      background-color: ${theme.color['surface/default']};
      color: ${theme.color['text/default']};
    `,
    tintBlack: css`
      box-shadow: inset 0 0 0 1px #13161999;
      background-color: ${theme.color['surface/neutral/default']};
      color: ${theme.color['text/default']};
    `,
    tintMagenta: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/magenta']};
      background-color: ${theme.color['decorativeSurface/weakest/magenta']};
      color: ${theme.color['decorativeText/weak/magenta']};
    `,
    tintSky: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/sky']};
      background-color: ${theme.color['decorativeSurface/weakest/sky']};
      color: ${theme.color['decorativeText/weak/sky']};
    `,
    tintOrange: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/orange']};
      background-color: ${theme.color['decorativeSurface/weakest/orange']};
      color: ${theme.color['decorativeText/weak/orange']};
    `,
    tintLightGreen: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/teal']};
      background-color: ${theme.color['decorativeSurface/weakest/teal']};
      color: ${theme.color['decorativeText/weak/teal']};
    `,

    // ===== OUTLINE VARIANTS =====
    outlineGray: css`
      box-shadow: inset 0 0 0 1px #878e9b;
      background-color: transparent;
      color: ${theme.color['text/weaker/default']};
    `,
    outlineGreen: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/strong/green']};
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/green']};
    `,
    outlineRed: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/strong/red']};
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/red']};
    `,
    outlineYellow: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/strong/yellow']};
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/yellow']};
    `,
    outlineBlue: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/strong/blue']};
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/blue']};
    `,
    outlinePurple: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/strong/purple']};
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/purple']};
    `,
    outlineLightGray: css`
      box-shadow: inset 0 0 0 1px #1316191a;
      background-color: transparent;
      color: ${theme.color['text/default']};
    `,
    outlineInverse: css`
      box-shadow: inset 0 0 0 1px ${theme.color['border/default']};
      background-color: transparent;
      color: ${theme.color['text/inverse/default']};
    `,
    outlineBlack: css`
      box-shadow: inset 0 0 0 1px #131619;
      background-color: transparent;
      color: ${theme.color['text/default']};
    `,
    outlineMagenta: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/strong/magenta']};
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/magenta']};
    `,
    outlineSky: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/strong/sky']};
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/sky']};
    `,
    outlineOrange: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/strong/orange']};
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/orange']};
    `,
    outlineLightGreen: css`
      box-shadow: inset 0 0 0 1px ${theme.color['decorativeBorder/strong/teal']};
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/teal']};
    `,

    // ===== GHOST VARIANTS =====
    ghostGray: css`
      background-color: transparent;
      color: ${theme.color['text/weaker/default']};
    `,
    ghostGreen: css`
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/green']};
    `,
    ghostRed: css`
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/red']};
    `,
    ghostYellow: css`
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/yellow']};
    `,
    ghostBlue: css`
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/blue']};
    `,
    ghostPurple: css`
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/purple']};
    `,
    ghostLightGray: css`
      background-color: transparent;
      color: ${theme.color['text/default']};
    `,
    ghostInverse: css`
      background-color: transparent;
      color: ${theme.color['text/inverse/default']};
    `,
    ghostBlack: css`
      background-color: transparent;
      color: ${theme.color['text/default']};
    `,
    ghostMagenta: css`
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/magenta']};
    `,
    ghostSky: css`
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/sky']};
    `,
    ghostOrange: css`
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/orange']};
    `,
    ghostLightGreen: css`
      background-color: transparent;
      color: ${theme.color['decorativeText/weak/teal']};
    `,
  };

  return styleMap[variantColorKey] || css``;
};

const getShapeStyles = (shape: BadgeProps['shape']) => {
  if (shape === 'circular') {
    return css`
      border-radius: 99px;
    `;
  }
  // shapeRectangular는 원본 theme.ts에 정의되지 않음 (기본 border-radius 사용)
  return css``;
};

const StyledBadge = styled('div', {
  target: badgeClasses.root,
})<{ ownerState: BadgeProps }>`
  /* theme.ts의 root 스타일 */
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 0 4px;
  border-radius: 2px;

  /* overridesResolver 로직을 조건부 스타일로 변환 */
  ${({ ownerState, theme }) => {
    const { variant, color, shape } = ownerState;

    return css`
      /* variant+color 조합 스타일 */
      ${color && getVariantColorStyles(variant, color, theme)}

      /* shape 스타일 */
      ${getShapeStyles(shape)}
    `;
  }}
`;

const StyledBadgeStartIcon = styled('div', {
  target: badgeClasses.startIcon,
})<{ ownerState?: BadgeProps }>`
  /* 원본 theme.ts의 startIcon 스타일 */
  display: flex;
  align-items: center;
  margin-right: 2px;

  & svg {
    font-size: 12px;
  }
`;

const StyledBadgeEndIcon = styled('div', {
  target: badgeClasses.endIcon,
})<{ ownerState?: BadgeProps }>`
  /* 원본 theme.ts의 endIcon 스타일 */
  display: flex;
  align-items: center;
  margin-left: 2px;

  & svg {
    font-size: 12px;
  }
`;

const StyledBadgeLabel = styled('div', {
  target: badgeClasses.label,
})<{ ownerState?: BadgeProps }>`
  /* theme.ts의 label 스타일 */
  display: inline-flex;
  align-items: center;
  font-weight: 500;
  font-size: 11px;
  text-align: center;

  &:not(&:has(svg)) {
    padding: 0 2px;
  }

  & svg {
    font-size: 12px;
  }
`;

export { StyledBadge, StyledBadgeEndIcon, StyledBadgeLabel, StyledBadgeStartIcon };
